'use client'

import { cn } from '../../../../../../shared/cn'
import 'katex/dist/katex.min.css'
import '../../ServerPreview/shared/mdx-components/CodeBlock/style.css'
import { MDXRemote, type MDXRemoteSerializeResult } from 'next-mdx-remote'
import { serialize } from 'next-mdx-remote/serialize'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import type { TreeData } from '../../../types'
import { type DbAttachment, mapAttachmentsToFiles, ViewAttachments } from '../../Attachments'
import { MarkdownRenderCompileTimeError } from '../../ServerPreview/Error/MarkdownRenderCompileTime'
import { MarkdownRenderRuntimeTimeError } from '../../ServerPreview/Error/MarkdownRenderRuntimeTime'
import { ErrorBoundary } from '../../ServerPreview/shared/MdxErrorBoundary'
import { mdxComponents } from '../../ServerPreview/shared/mdx-components'
import { getMdxPreviewMdxOptions } from '../../ServerPreview/shared/plugins'
import { syncScrollPlugin } from './_utils/customPlugin'

type ClientPreviewProps = {
  content: { markdown: string; attachments?: DbAttachment[] }
  onScroll?: () => void
  onMouseEnter?: () => void
  treeDataRef?: React.RefObject<TreeData | null>
  onReady?: () => void
  className?: string
}

export const ClientPreview = forwardRef<HTMLDivElement, ClientPreviewProps>(
  ({ content, onScroll, onMouseEnter, treeDataRef, onReady, className }, ref) => {
    const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult | null>(null)
    const [hasError, setHasError] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const lastScrollRef = useRef<number>(0)

    // save last scroll position
    const handleScroll = useCallback(() => {
      if (!hasError && ref && 'current' in ref && ref.current) {
        lastScrollRef.current = ref.current.scrollTop
      }
      onScroll?.()
    }, [hasError, onScroll, ref])

    const processMdx = useCallback(
      async (mdxContent: string) => {
        try {
          const syncScrollPluginCustom = syncScrollPlugin(treeDataRef, () => {
            // restore last scroll position
            if (!hasError && ref && 'current' in ref && ref.current) {
              ref.current.scrollTop = lastScrollRef.current
            }
          })
          // TODO: add <component> normalization
          const serialized = await serialize(mdxContent, {
            mdxOptions: getMdxPreviewMdxOptions({
              scrollSyncRehypePlugin: syncScrollPluginCustom
            }),
            parseFrontmatter: false
          })

          setMdxSource(serialized)
          setHasError(false)
          setErrorMessage('')
          onReady?.()
        } catch (error) {
          console.warn('MDX parse error:', error)
          setHasError(true)
          setErrorMessage(error instanceof Error ? error.message : 'Nieznany błąd składni')
        }
      },
      [ref, treeDataRef, hasError, onReady]
    )

    // process content when it changes
    useEffect(() => {
      void processMdx(content.markdown)
    }, [content, processMdx])

    return (
      <>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: preview panel uses scroll/mouseEnter for sync scrolling, not interactive content */}
        <div
          ref={ref}
          className={cn('custom-scrollbar h-full flex-1 overflow-auto py-1', className)}
          onScroll={handleScroll}
          onMouseEnter={onMouseEnter}
        >
          {hasError && (
            <MarkdownRenderCompileTimeError
              description='Nie udało się przetworzyć podglądu. Sprawdź poprawność składni lub komponentów.'
              message={errorMessage}
            />
          )}
          {!hasError && mdxSource && (
            <ErrorBoundary
              fallback={
                <MarkdownRenderRuntimeTimeError
                  description='Wystąpił błąd podczas renderowania. Upewnij się, że wszystkie użyte komponenty są dostępne w edytorze.'
                  message=''
                />
              }
            >
              <div className='space-y-6 text-contentSecondary'>
                <MDXRemote
                  {...mdxSource}
                  components={mdxComponents as Parameters<typeof MDXRemote>[0]['components']}
                />
              </div>
            </ErrorBoundary>
          )}
        </div>

        {content.attachments && (
          <div className='mt-4'>
            <ViewAttachments files={mapAttachmentsToFiles(content.attachments)} />
          </div>
        )}
      </>
    )
  }
)
ClientPreview.displayName = 'Preview'
